import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageSquare, Loader2, User, Send, Trash2, ArrowLeft, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);

  // Get the selected conversation object
  const activeConversation = conversations.find(c => c.oderId === selectedConvId);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    } else if (user) {
      fetchMessages();
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Group messages into conversations
    if (messages.length > 0 && user) {
      const convMap = new Map();
      
      messages.forEach(msg => {
        const otherUserId = msg.sender_id === user.user_id ? msg.recipient_id : msg.sender_id;
        const otherUserName = msg.sender_id === user.user_id ? msg.recipient_name : msg.sender_name;
        
        if (!convMap.has(otherUserId)) {
          convMap.set(otherUserId, {
            oderId: otherUserId,
            otherUserName: otherUserName,
            messages: [],
            lastMessage: null,
            unreadCount: 0
          });
        }
        
        const conv = convMap.get(otherUserId);
        conv.messages.push(msg);
        
        if (msg.recipient_id === user.user_id && !msg.read) {
          conv.unreadCount++;
        }
      });
      
      const convList = Array.from(convMap.values()).map(conv => {
        conv.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        conv.lastMessage = conv.messages[conv.messages.length - 1];
        return conv;
      });
      
      convList.sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));
      setConversations(convList);
    } else {
      setConversations([]);
    }
  }, [messages, user]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeConversation?.messages?.length]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const markConversationAsRead = async (conversation) => {
    const unreadMessages = conversation.messages.filter(
      m => m.recipient_id === user.user_id && !m.read
    );
    
    for (const msg of unreadMessages) {
      try {
        await axios.put(`${API}/messages/${msg.id}/read`, {}, { withCredentials: true });
      } catch (error) {
        console.error("Error marking message as read:", error);
      }
    }
    
    setMessages(prev => prev.map(m => 
      unreadMessages.find(um => um.id === m.id) ? { ...m, read: true } : m
    ));
  };

  const handleSelectConversation = (conv) => {
    setSelectedConvId(conv.oderId);
    if (conv.unreadCount > 0) {
      markConversationAsRead(conv);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: activeConversation.oderId,
        content: newMessage.trim()
      }, { withCredentials: true });
      
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    
    try {
      await axios.delete(`${API}/messages/${messageId}`, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.otherUserName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-4">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ height: 'calc(100vh - 96px)' }}>
        <div className="bg-void-surface border border-white/5 h-full flex rounded-lg overflow-hidden">
          
          {/* Conversations List (Left Panel) */}
          <div className={`w-full md:w-80 border-r border-white/5 flex flex-col ${selectedConvId ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-white/5">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-purple-light" />
                <h1 className="font-syne text-lg font-bold text-white">Messages</h1>
                {totalUnread > 0 && (
                  <span className="bg-purple text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {totalUnread}
                  </span>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="pl-9 bg-void border-white/10 text-white placeholder-slate-500"
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">
                    {searchQuery ? "No conversations found" : "No messages yet"}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    Start a conversation from the Member Directory
                  </p>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <button
                    key={conv.oderId}
                    onClick={() => handleSelectConversation(conv)}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-white/5 transition-colors text-left border-b border-white/5 ${
                      selectedConvId === conv.oderId ? 'bg-purple/10 border-l-2 border-l-purple' : ''
                    }`}
                  >
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-purple/20 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-purple-light" />
                      </div>
                      {conv.unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple text-white text-xs rounded-full flex items-center justify-center">
                          {conv.unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium truncate ${conv.unreadCount > 0 ? 'text-white' : 'text-slate-300'}`}>
                          {conv.otherUserName}
                        </span>
                        <span className="text-xs text-slate-500 flex-shrink-0 ml-2">
                          {formatTime(conv.lastMessage.created_at)}
                        </span>
                      </div>
                      <p className={`text-sm truncate ${conv.unreadCount > 0 ? 'text-slate-300 font-medium' : 'text-slate-500'}`}>
                        {conv.lastMessage.sender_id === user.user_id ? 'You: ' : ''}
                        {conv.lastMessage.content}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Thread (Right Panel) */}
          <div className={`flex-1 ${!selectedConvId ? 'hidden md:flex md:flex-col' : 'flex flex-col'}`}>
            {activeConversation ? (
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="h-16 p-4 border-b border-white/5 flex items-center gap-3 bg-white/5 shrink-0">
                  <button
                    onClick={() => setSelectedConvId(null)}
                    className="md:hidden text-slate-400 hover:text-white"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-purple/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-light" />
                  </div>
                  <div>
                    <h2 className="font-medium text-white">{activeConversation.otherUserName || "Unknown"}</h2>
                    <p className="text-xs text-slate-500">{activeConversation.messages?.length || 0} messages</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                  {activeConversation.messages && activeConversation.messages.length > 0 ? (
                    activeConversation.messages.map((msg, index) => {
                    const isMe = msg.sender_id === user.user_id;
                    const showDate = index === 0 || 
                      new Date(msg.created_at).toDateString() !== 
                      new Date(activeConversation.messages[index - 1].created_at).toDateString();
                    
                    return (
                      <div key={msg.id}>
                        {showDate && (
                          <div className="text-center my-4">
                            <span className="text-xs text-slate-500 bg-void px-3 py-1 rounded-full">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        )}
                        <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <div className="group max-w-[75%]">
                            <div
                              className={`px-4 py-2 rounded-2xl ${
                                isMe 
                                  ? 'bg-purple text-white rounded-br-sm' 
                                  : 'bg-white/10 text-slate-200 rounded-bl-sm'
                              }`}
                            >
                              <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                            </div>
                            <div className={`flex items-center gap-2 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                              <span className="text-xs text-slate-500">
                                {formatMessageTime(msg.created_at)}
                              </span>
                              {isMe && (
                                <button
                                  onClick={() => deleteMessage(msg.id)}
                                  className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                  ) : (
                    <p className="text-slate-500 text-center">No messages</p>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-white/5 shrink-0">
                  <div className="flex gap-3">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder={`Message ${activeConversation.otherUserName}...`}
                      rows={1}
                      className="flex-1 bg-void border-white/10 text-white placeholder-slate-500 resize-none min-h-[44px] max-h-32"
                      maxLength={1000}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="bg-purple hover:bg-purple-hover text-white self-end"
                    >
                      {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Your Messages</h3>
                  <p className="text-slate-400 text-sm max-w-xs">
                    Select a conversation to view messages, or start a new one from the Member Directory
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatMessageTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Export MessageDialog for other pages
export function MessageDialog({ isOpen, onClose, recipient, onSent }) {
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!content.trim()) return toast.error("Please enter a message");

    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: recipient.user_id,
        content: content.trim()
      }, { withCredentials: true });
      
      toast.success(`Message sent to ${recipient.name}`);
      setContent("");
      onClose();
      if (onSent) onSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-void-surface border border-white/10 p-6 max-w-md w-full rounded-lg"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-purple/20 flex items-center justify-center">
              <User className="w-5 h-5 text-purple-light" />
            </div>
            <div>
              <h3 className="font-syne font-bold text-white">Message {recipient.name}</h3>
              <p className="text-sm text-slate-400">Send a direct message</p>
            </div>
          </div>

          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your message..."
            rows={4}
            className="bg-void border-white/10 text-white placeholder-slate-500 mb-4"
            maxLength={1000}
          />

          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">{content.length}/1000</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="border-white/20 text-white hover:bg-white/10">
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={sending || !content.trim()} className="bg-purple hover:bg-purple-hover text-white">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Send</>}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
